import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Box, Stack, Typography } from "@mui/material";
import { Outlet } from "react-router";
import Block from "@/components/Block";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { STEPS, useStepNavigation } from "@/hooks/useStepNavigation";
import { formatDateRange, formatGuests, getCampsiteTypeLabel } from "@/utils/booking";

interface CollapsedSearchStepProps {
  label: string;
  value: string;
  isAvailable: boolean;
  onClick: () => void;
}

function CollapsedSearchStep({ label, value, isAvailable, onClick }: CollapsedSearchStepProps) {
  return (
    <Block
      sx={{ borderRadius: 10, opacity: value ? 1 : 0.5, flexShrink: 0, p: 2 }}
      elevation={0}
      onClick={isAvailable ? onClick : undefined}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">{label}</Typography>
        <Typography
          color="text.secondary"
          variant="h4"
          fontWeight="normal"
          noWrap
          sx={{ textTransform: "capitalize" }}
        >
          {value || t`Add`}
        </Typography>
      </Stack>
    </Block>
  );
}

export default function MobileSearchAccordion() {
  const { _, i18n } = useLingui();
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { currentStep, isStepAvailable, goToStep } = useStepNavigation();

  const steps: { name: keyof typeof STEPS; label: string; value: string }[] = [
    {
      name: "dates",
      label: _(msg`When?`),
      value: formatDateRange([state.dateFrom!, state.dateUntil!], i18n.locale),
    },
    {
      name: "accommodation",
      label: _(msg`How?`),
      value: getCampsiteTypeLabel(state.selectedCampsiteType, campgroundData.campsite_types),
    },
    {
      name: "guests",
      label: _(msg`Who?`),
      value: formatGuests(state.selectedPersons, campgroundData.campground?.persons || []),
    },
  ];

  return (
    <>
      {steps.map((step) =>
        step.name === currentStep ? (
          <Block
            key={step.name}
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              ...(currentStep === "dates" && { flex: 1 }),
            }}
            elevation={10}
          >
            <Typography variant="h3" sx={{ mx: 2, mt: 3, mb: 2, flexShrink: 0 }}>
              {i18n._(STEPS[currentStep].title)}
            </Typography>
            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              <Outlet />
            </Box>
          </Block>
        ) : (
          <CollapsedSearchStep
            key={step.name}
            label={step.label}
            value={step.value}
            isAvailable={isStepAvailable(step.name)}
            onClick={() => goToStep(step.name)}
          />
        ),
      )}
    </>
  );
}
